import APIClient from './apiClient';
import { safeStorage } from '../utils/safeStorage';

export interface DeviceTypeCount {
  device_type: string;
  count: number;
}

export interface ClientDeviceStats {
  client_id: string;
  client_name: string;
  /** The root client this row is an agency of, when it is one. */
  parent_id?: string | null;
  total_devices: number;
  active_devices: number;
  devices_needing_refresh: number;
  devices_by_type: DeviceTypeCount[];
}

export interface SystemStatus {
  timestamp: string;
  total_devices: number;
  total_clients: number;
  active_clients: number;
  devices_by_type: DeviceTypeCount[];
  devices_needing_refresh: number;
  clients_stats: ClientDeviceStats[];
  recent_devices_count: number;
  recent_updates_count: number;
}

/**
 * Lightweight version of SystemStatus returned by `/v2/status/summary`.
 * Has everything the Dashboard renders EXCEPT the per-client stats
 * array (up to ~1500 rows at production scale). Used exclusively by
 * DashboardPage; the Clients page still calls getSystemStatus().
 */
export interface SystemStatusSummary {
  timestamp: string;
  total_devices: number;
  total_clients: number;
  active_clients: number;
  devices_by_type: DeviceTypeCount[];
  devices_needing_refresh: number;
  recent_devices_count: number;
  recent_updates_count: number;
}

class StatusService {
  private apiClient: APIClient;

  constructor() {
    this.apiClient = APIClient.getInstance();
  }

  async getSystemStatus(opts?: { signal?: AbortSignal }): Promise<SystemStatus> {
    const config = this.apiClient.getConfiguration();
    const token = safeStorage.getItem('access_token');

    const response = await fetch(`${config.basePath}/v2/status`, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      signal: opts?.signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch system status: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Dashboard-only fast path. ~10× faster than getSystemStatus() at
   * production scale because the backend doesn't compute per-client
   * stats. Use this for the Dashboard; use getSystemStatus() only
   * when you need clients_stats (e.g. the Clients page).
   *
   * Resilience: if the backend doesn't yet expose /v2/status/summary
   * (frontend deploy landed before backend deploy), falls back to the
   * full /v2/status endpoint and projects it down to the summary
   * shape. This keeps the Dashboard working across any deploy order.
   */
  async getSystemStatusSummary(opts?: { signal?: AbortSignal }): Promise<SystemStatusSummary> {
    const config = this.apiClient.getConfiguration();
    const token = safeStorage.getItem('access_token');

    const response = await fetch(`${config.basePath}/v2/status/summary`, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      signal: opts?.signal,
    });

    // 404 means we're talking to an older backend that doesn't have
    // the summary endpoint yet. Fall back to /v2/status so the
    // Dashboard still renders — we just pay a little more wire/time.
    if (response.status === 404) {
      console.warn(
        '[STATUS SERVICE] /v2/status/summary not found on backend, ' +
        'falling back to /v2/status (frontend is ahead of backend deploy)'
      );
      const full = await this.getSystemStatus(opts);
      const { clients_stats, ...summary } = full;
      return summary;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch system status summary: ${response.statusText}`);
    }

    return await response.json();
  }
}

export const statusService = new StatusService();
